import * as XLSX from 'xlsx-js-style';
import AppTools from 'utils/app/appTools'


const DEFAULT_PRECISION = 1;  // decimals to show


const headerProperties = [
  "name",
  "unit",
  "last_rg_year",
  "last_rg_value",
  "comparisonEvoRg",
  "vs_eu27_year",
  "comparisonLvlEU",
  "comparisonEvoEU"
];

const DIMENSION_STYLES = {
  "obs-dimension-0": {
    color: "0078BB00",
    fill: "0078BB00"
  },
  "obs-dimension-1": {
    color: "3d9fce",
    fill: "3d9fce"
  },
  "obs-dimension-2": {
    color: "63bb6b",
    fill: "63bb6b"
  },
  "obs-dimension-3": {
    color: "fe8511",
    fill: "fe8511"
  }
};

const COMPARISON_ARROWS_UD = [ "\u2191", "\u2193", "=" ];
const COMPARISON_ARROWS_LR = [ "\u2192", "\u2190", "=" ];
const COMPARISON_PLUSMINUS = [ "+", "-", "=" ];

const comparisonChars = (count, strings) => {
  let resultString = "";
  if (count == null ) {
    return "";
  } else if ( count > 0 ) {
    resultString = strings[0].repeat(count);
  } else if ( count < 0 ) {
    resultString = strings[1].repeat(-count);
  } else {
    resultString = strings[2];
  }
  return resultString;
}

const cellStyle = (v, ignoreColor) => {
  if (v == null ) {
    return {};
  } else if ( ignoreColor ) {
    return { font: { bold: true }, alignment: { horizontal: 'center' } };
  } else if ( v > 0 ) {
    return { font: { bold: true, color: { rgb: "008000" } }, fill: { fgColor: { rgb: "CEF8CE" } }, alignment: { horizontal: 'center' } };
  } else if ( v < 0 ) {
    return { font: { bold: true, color: { rgb: "8B0000" } }, fill: { fgColor: { rgb: "F5B9B9" } }, alignment: { horizontal: 'center' } };
  } else {
    return { font: { bold: true, color: { rgb: "000000" } }, fill: { fgColor: { rgb: "E0E0E0" } }, alignment: { horizontal: 'center' } };
  }
}

const comparisonCell = (result, strings, ignoreColor) => {
  let v = comparisonChars(result, strings);
  let s = cellStyle(result, ignoreColor);
  return { v: v, t: "s", s: s };
}

const formattedNumberCell = (n, unit, minFractionDigits = 0) => {
  let z = "";
  if (unit.indexOf('%') >= 0) {
    z = "0." + "0".repeat(minFractionDigits + 1) + "%";
    n = n / 100;
  } else {
    z = "#,##0" + (minFractionDigits > 0 ? "." : "") + "0".repeat(minFractionDigits);
  }

  return { v: n, t: "n", z: z };
}

export const exportToExcel = (data, reportTableDefinition, header, lastUpdated, fileName = 'CAPVPanelReport.xlsx') => {
  const wsMetaData = [
    [
      {
        v: "Panel de diagnóstico del Informe de Competitividad del País Vasco",    
        t: "s",
        s: { font: { sz: 14, bold: true } }
      }
    ],
    [
      {
        v: "Orkestra - Instituto Vasco de Competitividad",    
        t: "s",
        s: { font: { sz: 11 } }
      }
    ],
    [],
    ["Fecha de generación", new Date()],
    ["Fecha de actualización", {
      v: lastUpdated
      // z: "yyyy-mm-dd hh:mm:ss"
    }
    ]
  ];

  const wsMeta = XLSX.utils.aoa_to_sheet( wsMetaData );

  const ws = XLSX.utils.aoa_to_sheet( [[]] );
  let row = 1;
  let merges = [];
  let mergedRowLen = headerProperties.length - 1;

  const headerWithStyle = header.map((t) => ({
    v: t,
    t: "s",
    s: { font: { bold: true } }
  }));

  XLSX.utils.sheet_add_aoa(ws, [ [ "", "", "", "", "", "Posicionamiento relativo UE27" ] ], {origin: `A${row}`});
  merges.push( { s: { c: 0, r: (row-1) }, e: { c: 4, r: (row-1) } } )
  merges.push( { s: { c: 5, r: (row-1) }, e: { c: mergedRowLen, r: (row-1) } } )
  row++;

  XLSX.utils.sheet_add_aoa(ws, [ headerWithStyle ], {origin: `A${row}`});
  row++;

  for(let table of reportTableDefinition) {
    let sections = table["items"];
    let title = AppTools.getSectionText( "report", [table.title_id], "title" );
    XLSX.utils.sheet_add_aoa(ws, [[title.toUpperCase()]], {origin: `A${row}`});
    merges.push( { s: { c: 0, r: (row-1) }, e: { c: mergedRowLen, r: (row-1) } } )
    ws[`A${row}`].s = { 
      font: { bold: true, color: { rgb: "FFFFFFFF" } },
      fill: { fgColor: { rgb: DIMENSION_STYLES[table.style]["fill"] } }  
    };
    row++;

    for(let section of sections) {
      let title = AppTools.getSectionText( "report", [section.title_id], "title" );
      XLSX.utils.sheet_add_aoa(ws, [[title]], {origin: `A${row}`});
      merges.push( { s: { c: 0, r: (row-1) }, e: { c: mergedRowLen, r: (row-1) } } )
      for (let c = 0; c <= mergedRowLen; c++ ) {
        const cellAddress = XLSX.utils.encode_cell({ r: (row-1), c: c });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { v: "", t: "s" };
        }
        ws[cellAddress].s = { 
          font: { bold: true, color: { rgb: DIMENSION_STYLES[table.style]["color"] } },
          border: { bottom: { style: 'thick', color: { rgb: DIMENSION_STYLES[table.style]["color"] } } }
        };
      }

      row++;

      let indicator_defs = section["items"].map(e => {
        let decimals = e.length > 2 ? e[2] : DEFAULT_PRECISION;
        let ignoreColor = section["ignoreColor"] || false;
        return {
          "id": e[1],
          "precision": decimals,
          "ignoreColor": ignoreColor
        };
      });

      for (let i = 0; i < indicator_defs.length; i++) {
        let dataRow = data[indicator_defs[i].id];
        let ignoreColor = indicator_defs[i].ignoreColor;
        let styleRow = [
          { v: dataRow["name"], t: "s" },
          { v: dataRow["unit"], t: "s" },
          { v: dataRow["last_rg_year"], t: "s" },
          formattedNumberCell(dataRow["last_rg_value"], dataRow["unit"], indicator_defs[i]["precision"]),
          comparisonCell(dataRow["comparisonEvoRg"], COMPARISON_ARROWS_LR, ignoreColor),
          { v: dataRow["vs_eu27_year"] || "", t: "s" },
          comparisonCell(dataRow["comparisonLvlEU"], COMPARISON_ARROWS_UD, ignoreColor),
          comparisonCell(dataRow["comparisonEvoEU"], COMPARISON_PLUSMINUS, ignoreColor)
        ];
        XLSX.utils.sheet_add_aoa(ws, [styleRow], {origin: `A${row}`});
        // XLSX.utils.sheet_add_json(ws, [styleRow], {origin: `A${row}`, header: headerProperties, skipHeader: true});
        row++;
      }
    }
  }
  ws["!merges"] = merges;
  ws['!cols'] = [
    { wch: 48 },
    { wch: 8 }
  ];

  // 2. Crear un libro de trabajo (workbook) vacío
  const workbook = XLSX.utils.book_new();

  // 3. Añadir la hoja al libro
  XLSX.utils.book_append_sheet(workbook, wsMeta, 'META');
  XLSX.utils.book_append_sheet(workbook, ws, 'PanelReport');

  // 4. Generar el archivo y disparar la descarga
  XLSX.writeFile(workbook, fileName);
};


// WEBPACK FOOTER //
// ./src/modules/Content/ReportTableContainer/ReportTableExcelHelper.js